import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';

import { checkDependencies } from '../../../services/dealGPT';
import { ProjectRunAnalysisQuestion, StepDependenciesOptions } from '../../../Types/dealGPT';

type ApplyChangesDialogProps = {
  open: boolean;
  question?: ProjectRunAnalysisQuestion;
  onClose: () => void;
  onConfirm: () => void;
  timeToProcess: string;
};

export default function ApplyChangesDialog({
  open,
  question,
  onClose,
  onConfirm,
  timeToProcess,
}: ApplyChangesDialogProps) {
  const [dependencyMap, setDependencyMap] = useState<Map<string, string[]>>();

  useEffect(() => {
    if (!open || !question) return;
    const getDependencies = async () => {
      try {
        const dependencies: StepDependenciesOptions | undefined = await checkDependencies(question);

        if (dependencies) {
          const uniqueDependencyMap: Map<string, Set<string>> = new Map();
          dependencies.dependencies.forEach((dependency) => {
            const key = dependency.pageName;
            if (!uniqueDependencyMap.has(key)) {
              uniqueDependencyMap.set(key, new Set());
            }

            const promptTypes = uniqueDependencyMap.get(key);
            if (promptTypes && dependency.promptType) {
              promptTypes.add(dependency.promptType);
            }
          });

          const result = new Map();
          uniqueDependencyMap.forEach((value, key) => {
            result.set(key, Array.from(value));
          });

          setDependencyMap(result);
        }
      } catch (error) {
        enqueueSnackbar('Failed to check dependencies. Please try again later.', {
          variant: 'error',
        });
        onClose();
      }
    };
    getDependencies();
  }, [open, question, onClose]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        Are you sure you want to apply changes?
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#757575',
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <DialogContentText>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ color: 'red', pb: 2 }}>
              {`Please be aware this process should take ${timeToProcess}.`}
            </Typography>
            <Typography>This will generate or regenerate the following dependencies:</Typography>
            <ul>
              {dependencyMap &&
                Array.from(dependencyMap.entries()).map(([key, value], index) => (
                  <li key={`dependency-${index}`}>
                    {value.length > 0 ? `${value.join(', ')} on page ` : ''}
                    {key}
                  </li>
                ))}
            </ul>
          </div>
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onConfirm} variant="contained">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
